<template>
  <figure class="scratchcard-display rounded-m m-none" :style="theme">
    <Component
      v-bind="{ ...$attrs, ...presentation.props, height, width }"
      :is="presentation.component"
      :alt="altText"
      class="rounded-m"
      @click="$emit('image-click')"
    />
    <div
      v-if="isCaptionVisible && price"
      class="price rounded-m text-center text-neutral-white py-4xs px-050 font-medium"
      :class="largeCaptionClass"
    >
      {{ formatMoney(price, { fractionDigits: 0 }) }}
    </div>

    <figcaption v-show="isCaptionVisible" class="caption-wrap flex justify-center">
      <span class="caption rounded-m text-center text-neutral-white py-025 px-100">
        <slot name="caption">
          <div class="caption-name font-medium text-uppercase" :class="largeCaptionClass">
            {{ name }}
          </div>

          <div v-if="jackpot" class="caption-amount" :class="smallCaptionClass">
            {{
              $t('scratchcard.teaser.button.winupto', {
                amount: formatMoney(jackpot, { fractionDigits: 0 }),
              })
            }}
          </div>
        </slot>
      </span>
    </figcaption>

    <slot />
  </figure>
</template>

<script lang="ts" setup>
import { LcImage } from '@lottocom/frontend-components'

import type { Money } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { LazyScratchcardDisplayFallback } from '#components'

import { ScratchcardColors } from '../constants/scratchcardColors'

type ScratchardDisplayProps = {
  fontSize?: 'lg' | 'md'
  height?: number
  isCaptionVisible?: boolean
  jackpot?: Maybe<Money>
  name: string
  price?: Maybe<Money>
  primaryColor?: Maybe<string>
  secondaryColor?: Maybe<string>
  src?: Maybe<string>
  width?: number
}

const { t } = useI18n()

const {
  fontSize = 'md',
  height = 260,
  isCaptionVisible = false,
  name,
  price,
  primaryColor = ScratchcardColors.TrustBlue900,
  secondaryColor = ScratchcardColors.FieryRed700,
  src = null,
  width = 260,
} = defineProps<ScratchardDisplayProps>()

type ScratchcardDisplayEmits = (e: 'image-click') => void
defineEmits<ScratchcardDisplayEmits>()

const presentation = computed(() => {
  if (src) {
    return {
      component: LcImage,
      props: {
        fetchpriority: 'low',
        preload: false,
        src,
      },
    }
  }

  return {
    component: LazyScratchcardDisplayFallback,
    props: { title: name },
  }
})
const theme = computed(() => ({
  '--primary-scratchcard-color': primaryColor ?? ScratchcardColors.TrustBlue900,
  '--secondary-scratchcard-color': secondaryColor ?? ScratchcardColors.FieryRed700,
}))

const altText = computed(() =>
  price
    ? t('scratchcard.teaser.image.alt.with.price', {
        name: name,
        price: formatMoney(price, { fractionDigits: 0 }),
      })
    : t('scratchcard.teaser.image.alt.without.price', { name }),
)

const largeCaptionClass = computed(() => ({
  'caption-bold': fontSize === 'md',
  'headline-h5-headline': fontSize === 'lg',
}))

const smallCaptionClass = computed(() => ({
  'paragraph-regular': fontSize === 'lg',
  'paragraph-small-text': fontSize === 'md',
}))
</script>

<style lang="scss" scoped>
$element-position: 8%;

.scratchcard-display {
  position: relative;
  width: 100%;
  box-shadow: token('shadow/shadow/card/scratchcards');

  &.outlined {
    border: token('size/border/100') solid token('color/transparent/neutral/40/000');
  }

  .price {
    position: absolute;
    top: $element-position;
    left: $element-position;
    border: var(--secondary-scratchcard-color) token('size/border/200') solid;
    background: var(--primary-scratchcard-color);
  }

  .caption {
    border: var(--secondary-scratchcard-color) token('size/border/200') solid;
    background: var(--primary-scratchcard-color);
    overflow-wrap: break-word;

    &-wrap {
      position: absolute;
      right: $element-position;
      bottom: $element-position;
      left: $element-position;
    }
  }

  .caption-amount {
    opacity: 0.8;
  }
}
</style>
